import { useState, useEffect } from 'react'
import { TableFooter } from '../TableFooter'
import { TableRow } from '../TableRow'
import { TableHeader } from '../TableHeader'
import { TableType } from './types'

export const Table = <T extends { [key: string]: any }>({
  rows,
  columns,
  stickyHeader,
  FooterContent,
  className,
  fixedCellSize,
  ExpandableRow,
  onSelect,
  onClick,
  onSort,
  defaultSorted,
  onFilter,
  clicked,
  showRemoveSortButton,
}: TableType<T>) => {
  const [currentSelected, setCurrentSelected] = useState<number[]>([])

  const formatRows = () =>
    rows.map((row, index) => {
      return { tableRowId: index, ...row }
    })

  // Function for individual row selection
  const handleSelectRow = (row: T, checked: boolean) => {
    if (!checked) {
      setCurrentSelected((previous) =>
        previous.filter((rowId) => rowId !== row.tableRowId),
      )
    } else setCurrentSelected((previous) => [...previous, row.tableRowId])
  }

  // Function for TableHeader to call when 'select all' checkbox is checked
  const handleSelectAll = () => {
    if (currentSelected.length === rows.length) {
      setCurrentSelected([])
    } else {
      const allRowIds = []
      for (let i = 0; i <= rows.length - 1; i += 1) {
        allRowIds.push(i)
      }
      setCurrentSelected(allRowIds)
    }
  }

  // Determines colspan including all columns + checkboxes + expandable row icon
  const getColspan = () => {
    let num = columns.length
    if (ExpandableRow) num += 1
    if (onSelect) num += 1
    return num
  }

  // Calls onSelect function when currentSelected changes
  useEffect(() => {
    if (onSelect) {
      const selectedRows = formatRows().filter((row) =>
        currentSelected.includes(row.tableRowId),
      )
      onSelect(selectedRows)
    }
  }, [currentSelected])

  return (
    <div className="TableWrapper">
      <table
        className={`Table ${className || ''} ${
          fixedCellSize ? 'table-fixed' : 'table-auto'
        }`}
      >
        <TableHeader
          columns={columns}
          stickyHeader={stickyHeader}
          ExpandableRow={!!ExpandableRow}
          handleSelectAll={onSelect && handleSelectAll}
          selectedAll={onSelect && currentSelected.length === rows.length}
          onSort={onSort}
          defaultSorted={defaultSorted}
          onFilter={onFilter}
          showRemoveSortButton={showRemoveSortButton}
        />
        <tbody className="Table__body bg-base">
          {stickyHeader && <tr className="TableRow__spacer" />}
          {formatRows().map((row, index) => {
            return (
              <TableRow
                key={index}
                row={row}
                columns={columns}
                ExpandableRow={ExpandableRow}
                onClick={onClick}
                selected={
                  (clicked && clicked.tableRowId === row.tableRowId) ||
                  (clicked?.id && clicked.id === row.id) ||
                  (onSelect && currentSelected.includes(row.tableRowId))
                }
                handleSelectRow={onSelect && handleSelectRow}
                getColspan={getColspan}
              />
            )
          })}
        </tbody>
        {FooterContent && (
          <TableFooter colSpan={getColspan()}>{FooterContent}</TableFooter>
        )}
      </table>
    </div>
  )
}
